import React, { useState } from "react";
import { useTheme } from "@/context/ThemeContext";
import { companyInfo } from "@/data/mockData";
import { X, Send, CheckCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export default function QuoteModal({ open, onClose }) {
  const { isDark } = useTheme();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    product: "",
    quantity: "",
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Build mailto link
    const subject = `Quote Request from ${form.name} - ${form.product}`;
    const body = `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nProduct: ${form.product}\nQuantity: ${form.quantity}\n\nMessage:\n${form.message}`;
    window.open(
      `mailto:${companyInfo.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    );
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setForm({ name: "", email: "", phone: "", product: "", quantity: "", message: "" });
      onClose();
    }, 2000);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={`max-w-lg ${
          isDark
            ? "bg-slate-900 border-amber-600/20 text-white"
            : "bg-white border-gray-200 text-slate-900"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-2xl font-bold ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            Request a Quote
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
            <p className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Quote Request Sent!
            </p>
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              We'll get back to you shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Name *
                </label>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                  className={isDark ? "bg-slate-800 border-amber-600/20 text-white" : ""}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Phone *
                </label>
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  placeholder="Your phone"
                  className={isDark ? "bg-slate-800 border-amber-600/20 text-white" : ""}
                />
              </div>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Email
              </label>
              <Input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className={isDark ? "bg-slate-800 border-amber-600/20 text-white" : ""}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Product *
                </label>
                <select
                  name="product"
                  value={form.product}
                  onChange={handleChange}
                  required
                  className={`flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
                    isDark
                      ? "bg-slate-800 border-amber-600/20 text-white"
                      : "bg-white border-gray-300 text-slate-900"
                  }`}
                >
                  <option value="">Select product</option>
                  <option>Submersible Wires - PolyMac</option>
                  <option>Submersible Wires - Poly Om</option>
                  <option>Submersible Wires - Poly Kiran</option>
                  <option>Bare Copper Wires</option>
                  <option>Earthing Wire</option>
                  <option>CCR Wires</option>
                  <option>Annealed Copper Wire</option>
                  <option>Copper Rods - CC Grade</option>
                  <option>Copper Rods - Commercial Grade</option>
                  <option>Copper Rods - CCR</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Quantity
                </label>
                <Input
                  name="quantity"
                  value={form.quantity}
                  onChange={handleChange}
                  placeholder="e.g., 500 kg"
                  className={isDark ? "bg-slate-800 border-amber-600/20 text-white" : ""}
                />
              </div>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Message
              </label>
              <Textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={3}
                placeholder="Tell us about your requirements..."
                className={`resize-none ${isDark ? "bg-slate-800 border-amber-600/20 text-white" : ""}`}
              />
            </div>
            <Button
              type="submit"
              className="w-full py-5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300"
            >
              Send Quote Request
              <Send className="ml-2 w-4 h-4" />
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
